import { Action, ActionReducer, ActionReducerMap, combineReducers, createReducer, MetaReducer, on } from '@ngrx/store';
import {
    createFormGroupState,
    formGroupReducer,
    FormGroupState,
    setValue
} from 'ngrx-forms';
import { SystemSettings, SystemSettingsState } from '../../../../core/state/system-settings.state';
import { ActionType, isAction } from '../../../../shared/state/index.state';
import { AdminSystemSettingsActions } from './admin-system-settings-actions.service';
import {
    SystemSettingsFormState,
    SystemSettingsFormValue
} from './admin-system-settings.state';

export const SYSTEM_SETTINGS_FORM_ID = 'systemSettingsForm contained in SystemSettingsState';

const initialSystemSettingsValue: SystemSettingsState = {
    settings: {
        inspireNotificationActive: false,
        inspireNotificationText: ''
    }
};

export const INITIAL_FORM_STATE: FormGroupState<SystemSettingsFormValue> =
    createFormGroupState<SystemSettingsFormValue>(SYSTEM_SETTINGS_FORM_ID, initialSystemSettingsValue);

const systemSettingsFormStateReducer = (state = INITIAL_FORM_STATE, action: Action): FormGroupState<SystemSettingsFormValue> => {
    // First handle API actions - before form actions (that are handled by formGroupReducer)
    if (isAction(action as ActionType<any>, AdminSystemSettingsActions.GET_SYSTEM_SETTINGS_SUCCESS_FROM_ADMIN)) {
        const settings: SystemSettings = (action as ActionType<any>).payload;
        const formValue: SystemSettingsFormValue = {
            settings: {
                inspireNotificationActive: (settings !== null ? settings.inspireNotificationActive : undefined)
                    || initialSystemSettingsValue.settings.inspireNotificationActive,
                inspireNotificationText: (settings !== null ? settings.inspireNotificationText : undefined)
                    || initialSystemSettingsValue.settings.inspireNotificationText
            }
        };

        return setValue(formValue)(state);
    }

    if (isAction(action as ActionType<any>, AdminSystemSettingsActions.GET_SYSTEM_SETTINGS_FAIL)) {
        // This will change state to isUnsubmitted=true right at the time we get the error from the backend
        // return markAsUnsubmitted(state);
        // This has same effect - and dispite the name it does not "emit" the action to be seen in the Redux Dev Tools
        // return formStateReducer(state, new MarkAsUnsubmittedAction(SYSTEM_SETTINGS_FORM_ID));
        // In both cases above, the change will be displayed as part of the SAVE_SYSTEM_SETTINGS_FAIL action change
    }

    // Then handle form change states

    return formGroupReducer(state, action);
};

const loadingReducer = (state = false, action: Action): boolean => {

    if (isAction(action as ActionType<any>,
        AdminSystemSettingsActions.GET_SYSTEM_SETTINGS_FROM_ADMIN,
        AdminSystemSettingsActions.SAVE_SYSTEM_SETTINGS)) {
        return true;
    }

    if (isAction(action as ActionType<any>,
        AdminSystemSettingsActions.GET_SYSTEM_SETTINGS_SUCCESS_FROM_ADMIN,
        AdminSystemSettingsActions.GET_SYSTEM_SETTINGS_FAIL,
        AdminSystemSettingsActions.SAVE_SYSTEM_SETTINGS_SUCCESS,
        AdminSystemSettingsActions.SAVE_SYSTEM_SETTINGS_FAIL)) {
        return false;
    }

    return state;
};

/**
 * All combineReducers() does is generate a function that calls your reducers with the slices of state selected
 * according to their keys, and combines their results into a single object again. It's not magic.
 * And like other reducers, combineReducers() does not create a new object if all of the reducers
 * provided to it do not change state.
 */
const systemSettingsStateMetaReducer: ActionReducer<SystemSettingsFormState['systemSettingsForm'], any> =
    combineReducers<SystemSettingsFormState['systemSettingsForm'], any>({
        formState: systemSettingsFormStateReducer,
        loading: loadingReducer
    });

/**
 * Due to AOT constraints we can't call functions like combineReducers() in StoreModule.forFeature(...) when
 * defining metadata. This compiler to statically analyse the code.
 * In stead we create the meta reducer once while the exported reducer is called for every action.
 * https://github.com/ngrx/platform/blob/master/docs/store/api.md#feature-module-reducers-and-aot
 */
// tslint:disable-next-line:only-arrow-functions (AOT required named function - not arrow notation)
export function systemSettingsStateReducers(state, action): any {
    return systemSettingsStateMetaReducer(state, action);
}

export const systemSettingsStateMetaReducer2: ActionReducerMap<any> = {
    formState: systemSettingsFormStateReducer,
    loading: loadingReducer
};
